import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Target, Calculator, IndianRupee, TrendingUp, Shield, GraduationCap, Heart, Home, LineChart } from 'lucide-react';

const CalculatorsHubPage = () => {
  const calculatorCategories = [
    {
      icon: Target,
      title: 'Goal Planner',
      description: 'Plan any financial goal with precision. Calculate exactly how much you need to invest monthly.',
      link: '/calculators/goal-planner',
      tools: ['Custom Goals', 'Timeline Planning', 'SIP Calculator'],
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: IndianRupee,
      title: 'Crorepati Calculator',
      description: 'Plan your path to ₹1 crore by estimating investment amounts, returns, and timelines.',
      link: '/calculators/crorepati',
      tools: ['Target Amount', 'Time Horizon', 'Monthly SIP'],
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Calculator,
      title: 'SIP Calculator',
      description: 'Calculate future value of your SIP investments with compound growth projections.',
      link: '/calculators/sip',
      tools: ['Future Value', 'Wealth Projection', 'Visual Breakdown'],
      gradient: 'from-blue-500 to-purple-500'
    },
    {
      icon: TrendingUp,
      title: 'SIP vs EMI',
      description: 'Compare wealth creation through SIP investing versus spending the same on EMI.',
      link: '/calculators/sip-emi',
      tools: ['Side-by-Side Comparison', 'Interest Analysis', 'Net Worth Impact'],
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: Heart,
      title: 'Marriage Planning',
      description: 'Plan your dream wedding without financial stress. Start saving systematically!',
      link: '/calculators/marriage',
      tools: ['Wedding Budget', 'Expense Breakdown', 'Investment Strategy'],
      gradient: 'from-rose-500 to-pink-500'
    },
    {
      icon: GraduationCap,
      title: 'Education Planning',
      description: 'Secure your child\'s future with smart education planning. Beat education inflation!',
      link: '/calculators/education',
      tools: ['India & Abroad Options', 'Age-based Planning', 'Inflation Adjusted'],
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Home,
      title: 'House Planning',
      description: 'Build your dream home fund. Calculate down payment and registration costs.',
      link: '/calculators/house',
      tools: ['Down Payment', 'Property Appreciation', 'Loan Estimate'],
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      icon: Shield,
      title: 'Emergency Fund Builder',
      description: 'Build your financial safety net. Be prepared for life\'s unexpected moments!',
      link: '/calculators/emergency',
      tools: ['3-12 Months Coverage', 'Progress Tracker', 'Investment Tips'],
      gradient: 'from-amber-500 to-orange-500'
    },
    {
      icon: LineChart,
      title: 'Portfolio Forecasting',
      description: 'AI-powered portfolio analysis with multi-scenario forecasting and risk assessment.',
      link: '/calculators/portfolio-forecast',
      tools: ['Asset Allocation', 'Scenario Analysis', 'Risk Profiling'],
      gradient: 'from-violet-500 to-purple-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Financial Calculators
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Frontier Investments&apos; interactive calculators empower you to plan effectively. Take control of your financial journey with informed, goal-oriented decisions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
            {calculatorCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <Link
                  key={index}
                  to={category.link}
                  className="group bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all duration-300 hover:transform hover:scale-[1.02]"
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r ${category.gradient} rounded-xl mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{category.title}</h3>
                  <p className="text-white/60 text-sm mb-4 line-clamp-2">{category.description}</p>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {category.tools.map((tool, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-white/5 rounded-full text-xs text-white/50">
                        {tool}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center text-white text-sm group-hover:translate-x-1 transition-transform">
                    <span>Calculate Now</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            <Link
              to="/calculators/goal-planning"
              className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-xl rounded-2xl p-8 border border-purple-500/30 hover:border-purple-500/50 transition-all"
            >
              <h3 className="text-2xl font-bold text-white mb-3">All Goal Planning Calculators</h3>
              <p className="text-white/70 mb-4">
                Complete suite of goal-based calculators for marriage, education, house purchase, and emergency planning.
              </p>
              <div className="flex items-center text-purple-400">
                <span>View All Goal Calculators</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
            <Link
              to="/calculators/financial-planning"
              className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur-xl rounded-2xl p-8 border border-blue-500/30 hover:border-blue-500/50 transition-all"
            >
              <h3 className="text-2xl font-bold text-white mb-3">Retirement Planner</h3>
              <p className="text-white/70 mb-4">
                Advanced tools for retirement planning, portfolio analysis, and long-term investment strategies.
              </p>
              <div className="flex items-center text-blue-400">
                <span>Explore Retirement Planner</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          </div>

          <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Need Personalized Financial Planning?</h2>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Our expert advisors can help you create a comprehensive financial plan tailored to your unique goals and circumstances.
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all duration-300"
            >
              Speak with an Advisor
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CalculatorsHubPage;
